import { CoreError, CoreErrorCause } from "../../../domain/models/CoreError";

export enum ImageUploadErrorCause {
  ImageConversionFailed = "image_conversion_failed",
  UploadFailed = "upload_failed",
  DownloadURLNotFound = "download_url_not_found",
  PermissionDenied = "permission_denied",
  NetworkError = "network_error",
}

export class ImageUploadErrorConverter {
  /**
   * Converts Firebase Storage errors to CoreError with image upload-specific causes.
   */
  static domainModel(error: unknown): CoreError {
    if (error instanceof CoreError) {
      return error;
    }

    // Firebase Storage error codes
    const err = (error ?? {}) as { code?: string; message?: string };
    const code = err.code || "";
    const message = err.message || "Unknown error";

    switch (code) {
      case "storage/unauthorized":
      case "storage/permission-denied":
        return new CoreError({
          cause: CoreErrorCause.PermissionDenied,
          context: `Permission denied: ${message}`,
          underlyingException: error,
        });

      case "storage/retry-limit-exceeded":
      case "storage/unauthenticated":
        return new CoreError({
          cause: CoreErrorCause.NetworkError,
          context: `Network error: ${message}`,
          underlyingException: error,
        });

      case "storage/object-not-found":
        return new CoreError({
          cause: CoreErrorCause.DataNotFound,
          context: `Object not found: ${message}`,
          underlyingException: error,
        });

      case "storage/quota-exceeded":
        return new CoreError({
          cause: CoreErrorCause.Unknown,
          context: `Storage quota exceeded: ${message}`,
          underlyingException: error,
        });

      case "storage/canceled":
        return new CoreError({
          cause: CoreErrorCause.Unknown,
          context: `Upload canceled: ${message}`,
          underlyingException: error,
        });

      default:
        return new CoreError({
          cause: CoreErrorCause.Unknown,
          context: message,
          underlyingException: error,
        });
    }
  }

  /**
   * Creates an image upload-specific error.
   */
  static createImageUploadError(
    cause: ImageUploadErrorCause,
    context: string,
  ): CoreError {
    let coreErrorCause: CoreErrorCause;

    switch (cause) {
      case ImageUploadErrorCause.ImageConversionFailed:
        coreErrorCause = CoreErrorCause.Unknown;
        break;
      case ImageUploadErrorCause.UploadFailed:
        coreErrorCause = CoreErrorCause.NetworkError;
        break;
      case ImageUploadErrorCause.DownloadURLNotFound:
        coreErrorCause = CoreErrorCause.DataNotFound;
        break;
      case ImageUploadErrorCause.PermissionDenied:
        coreErrorCause = CoreErrorCause.PermissionDenied;
        break;
      case ImageUploadErrorCause.NetworkError:
        coreErrorCause = CoreErrorCause.NetworkError;
        break;
      default:
        coreErrorCause = CoreErrorCause.Unknown;
    }

    return new CoreError({
      cause: coreErrorCause,
      context,
    });
  }
}
